import { parseCsClasses } from '../CsImport/CsClassParser';
import { buildClassShapeFromParsedCs } from '../CsImport/CsClassShapeFactory';

import { buildAssociationConnectors } from './AssociationConnectorBuilder';
import inheritanceConnectorBuilder from './InheritanceConnectorBuilder';
import { filterDuplicateClasses } from './CsImportDuplicateFilter';
import { derivePackageName, resolveOrigin, buildOrGrowPackageShape } from './CsImportPackagePlacement';

const SHAPE_GAP = 24; // horizontal gap between staggered shapes, on top of each shape's own width

/**
 * Builds one 'association' connector per constructor DEPENDENCY (param) that
 * resolves to another class/interface shape — either one built from the SAME
 * paste, or one ALREADY on the canvas from an earlier import (existingShapes).
 * Thin wrapper around AssociationConnectorBuilder.buildAssociationConnectors
 * (shared with the "Associate" button's full-canvas scan, see
 * UmlSchemaImportTab.jsx) — just normalizes parsedClasses+shapes and
 * existingShapes into the {id, name, members}[] shape that function expects.
 * @param {object[]} parsedClasses
 * @param {import('../../Types/UmlClassShape').default[]} shapes - newly built shapes, same order/index as parsedClasses
 * @param {import('../../Types/UmlClassShape').default[]} existingShapes - top-level shapes already on the canvas
 * @param {import('../../Types/UmlConnector').default[]} existingConnectors - connectors already on the canvas
 * @returns {import('../../Types/UmlConnector').default[]}
 */
function buildDependencyConnectors(parsedClasses, shapes, existingShapes, existingConnectors) {
    const existingEntries = existingShapes.map(function toExistingEntry(shape) {
        return { id: shape.id, name: shape?.caption?.text, members: shape.members };
    });
    const newEntries = parsedClasses.map(function toNewEntry(parsed, index) {
        return { id: shapes[index].id, name: parsed.name, members: parsed.members };
    });
    // existingEntries FIRST — buildAssociationConnectors keeps the first name it
    // sees per name, so an already-on-canvas class wins a name collision over a
    // freshly (re-)pasted one with the same name.
    return buildAssociationConnectors([...existingEntries, ...newEntries], existingConnectors);
}

/**
 * Normalizes parsedClasses+shapes into the {id, name, inherits}[] entry shape
 * that InheritanceConnectorBuilder expects — same adapter pattern as
 * buildDependencyConnectors above, so both connector kinds get the shared,
 * de-duped, warning-aware builder instead of a local re-implementation.
 * @param {object[]} parsedClasses
 * @param {import('../../Types/UmlClassShape').default[]} shapes
 * @returns {{id: string, name: string, inherits: string[]}[]}
 */
function toInheritanceEntries(parsedClasses, shapes) {
    return parsedClasses.map(function toEntry(parsed, index) {
        return { id: shapes[index].id, name: parsed.name, inherits: parsed.inherits || [] };
    });
}

/**
 * Adapter between the generic import registry and the existing C# parser
 * (Core/CsImport/*) — one shape per class/interface found in the paste (minus
 * any duplicate names already on the canvas, see CsImportDuplicateFilter),
 * plus an inheritance connector for every base type that resolves to a known
 * shape, plus an association connector for every constructor dependency that
 * resolves to a known shape (in the paste OR already on the canvas). Package
 * placement (new package vs. growing an existing one) is delegated to
 * CsImportPackagePlacement — see that file for the tryAdopt/containment
 * rationale.
 * @param {string} source
 * @param {number} x
 * @param {number} y
 * @param {import('../../Types/UmlClassShape').default[]} existingShapes - top-level shapes already on the canvas, for dependency-resolution against classes imported earlier
 * @param {import('../../Types/UmlConnector').default[]} existingConnectors - connectors already on the canvas, for de-duping repeat imports
 * @param {{collapsed?: boolean}} [options] - collapsed: build every new class shape already collapsed (used by the Files-tab import, see useFileImportGenerate.js)
 * @returns {{shapes: import('../../Types/UmlClassShape').default[], connectors: import('../../Types/UmlConnector').default[], warnings: string[]}}
 */
export function runCsImport(source, x, y, existingShapes = [], existingConnectors = [], options = {}) {
    const { collapsed = false } = options;
    const allParsedClasses = parseCsClasses(source);
    const namespace = allParsedClasses.namespace;
    const parsedClasses = filterDuplicateClasses(allParsedClasses, existingShapes);

    const packageName = derivePackageName(namespace);
    const { originX, originY, packageOrigin, existingPackage, buildNewPackage } = resolveOrigin(existingShapes, packageName, x, y);

    const shapes = [];
    let nextX = originX;
    parsedClasses.forEach(function buildShape(parsed) {
        const shape = buildClassShapeFromParsedCs(parsed, nextX, originY, collapsed);
        shapes.push(shape);
        nextX += shape.w + SHAPE_GAP;
    });

    const packageShape = buildOrGrowPackageShape({
        buildNewPackage,
        existingPackage,
        packageOrigin,
        packageName,
        shapes,
        originX,
        originY,
        contentRight: nextX - SHAPE_GAP,
    });

    const connectors = [
        ...inheritanceConnectorBuilder.buildInheritanceConnectors(toInheritanceEntries(parsedClasses, shapes), existingConnectors),
        ...buildDependencyConnectors(parsedClasses, shapes, existingShapes, existingConnectors),
    ];
    const warnings = parsedClasses.flatMap(function collectWarnings(parsed) {
        return parsed.skipped.map(function toWarningText(skip) {
            return `${parsed.name}.${skip.name}: ${skip.reason}`;
        });
    });

    const allShapes = packageShape ? [packageShape, ...shapes] : shapes;
    return { shapes: allShapes, connectors, warnings };
}
